<template>
    <div class="employee-selector">
        <div class="d-flex align-center mb-1">
            <div class="subtitle-2 font-weight-medium">{{ label }}</div>
            <v-spacer />

            <v-menu
                v-show="available_to_add.length > 0 && !is_all_employees_selected"
                v-model="menu_open"
                :close-on-content-click="false"
                min-width="400"
                offset-y
            >
                <template #activator="{ on, attrs }">
                    <v-btn
                        class="inline-action"
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        small
                        text
                    >
                        <v-icon
                            aria-hidden="true"
                            small
                        >mdi-plus</v-icon>
                        Add
                    </v-btn>
                </template>

                <v-card min-width="350">
                    <v-card-title class="pb-2">
                        <div class="d-flex align-center gap-2 full-width">
                            <v-text-field
                                v-model="add_search_query"
                                placeholder="Search users or teams to add"
                                prepend-inner-icon="mdi-magnify"
                                class="flex-grow-1"
                                outlined
                                dense
                                hide-details
                                clearable
                                autofocus
                            />
                        </div>
                    </v-card-title>

                    <v-divider />

                    <v-card-text
                        class="pa-0"
                        style="max-height: 300px; overflow-y: auto;"
                    >
                        <v-list dense>
                            <template v-if="filtered_available_employees.length === 0">
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-list-item-title class="text-center grey--text">
                                            No users found
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>

                            <template v-else>
                                <div
                                    v-for="(group, index) in grouped_available_employees"
                                    :key="`group-${group.kind}`"
                                >
                                    <v-subheader class="text-uppercase text-caption font-weight-bold">
                                        {{ group.label }}
                                    </v-subheader>

                                    <v-list-item
                                        v-for="employee in group.items"
                                        :key="employee.selected_object_id"
                                        @click="addEmployee(employee.selected_object_id)"
                                    >
                                        <v-list-item-icon>
                                            <v-icon
                                                aria-hidden="true"
                                                color="grey darken-2"
                                                small
                                            >{{ getIconForKind(employee.kind) }}</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title>{{ employee.label }}</v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>

                                    <v-divider
                                        v-if="index < grouped_available_employees.length - 1"
                                        class="my-1"
                                    />
                                </div>
                            </template>
                        </v-list>
                    </v-card-text>
                </v-card>
            </v-menu>

            <v-btn
                v-if="selected_employees.length > 0"
                class="inline-action"
                color="error"
                small
                text
                @click="removeAll"
            >
                <v-icon
                    aria-hidden="true"
                small
                >mdi-close</v-icon>
                Remove All
            </v-btn>
        </div>

        <div
            v-if="selected_employees.length === 0"
            class="empty-state pa-4 text-center"
        >
            <v-icon
                color="grey lighten-1"
                size="48"
                aria-hidden="true"
            >mdi-account-off-outline</v-icon>
            <div class="text-body-2 grey--text text--darken-1 mt-2">
                No users selected
            </div>
        </div>

        <div
            v-else
            class="chips-container"
        >
            <v-chip
                v-for="employee in selected_employees"
                :key="employee.selected_object_id"
                color="grey darken-2"
                class="ma-1"
                close
                outlined
                @click:close="removeEmployee(employee.selected_object_id)"
            >
                <v-icon
                    aria-hidden="true"
                    left
                    small
                >{{ getIconForKind(employee.kind) }}</v-icon>
                {{ employee.label }}
            </v-chip>
        </div>

        <div
            v-if="hint"
            class="text-caption grey--text mt-2"
        >
            {{ hint }}
        </div>
    </div>
</template>

<script>
export default {
    name: 'EmployeeSelector',

    props: {
        value: {
            type: Array,
            default: () => [],
        },
        label: {
            type: String,
            default: 'Select Employees',
        },
        hint: {
            type: String,
            default: '',
        },
        organization_id: {
            type: String,
            required: false,
        },

        /**
         * If provided, this array will be used as the source of employee options.
         * If null, the component will load employee options from the server via loadEmployees()
         */
        employee_options: {
            type: Array,
            default: null,
        },
    },

    data() {
        return {
            internal_all_employees: [],
            add_search_query: '',
            menu_open: false,
            loading: false,
        };
    },

    computed: {
        all_employees() {
            if (this.employee_options != null) {
                return this.processEmployeeOptions(this.employee_options);
            }
            return this.internal_all_employees;
        },

        selected_employees() {
            return this.all_employees.filter(emp =>
                this.value.includes(emp.selected_object_id)
            );
        },

        available_to_add() {
            return this.all_employees.filter(
                emp => !this.value.includes(emp.selected_object_id)
            );
        },

        filtered_available_employees() {
            if (!this.add_search_query) {
                return this.available_to_add;
            }
            const query = this.add_search_query.toLowerCase();
            return this.available_to_add.filter(emp =>
                emp.label.toLowerCase().includes(query)
            );
        },

        grouped_available_employees() {
            const groups = {
                'all-employees': { kind: 'all-employees', label: 'Organization', items: [] },
                'group': { kind: 'group', label: 'Teams', items: [] },
                'employee': { kind: 'employee', label: 'Users', items: [] },
            };

            this.filtered_available_employees.forEach(emp => {
                const kind = emp.kind || 'employee';
                if (groups[kind]) {
                    groups[kind].items.push(emp);
                }
            });

            return Object.values(groups).filter(group => group.items.length > 0);
        },

        all_selected() {
            return this.selected_employees.length === this.all_employees.length;
        },

        is_all_employees_selected() {
            return this.selected_employees.some(emp => emp.kind === 'all-employees');
        },
    },

    mounted() {
        if (this.employee_options == null) {
            this.loadEmployees();
        } else {
            this.setDefaultSelection();
        }
    },

    watch: {
        employee_options: {
            handler() {
                if (this.employee_options != null) {
                    this.setDefaultSelection();
                }
            },
            immediate: false,
        },
    },

    methods: {
        processEmployeeOptions(options) {
            const all_selector_options = options || [];

            return all_selector_options
                .filter(option => !option.is_archived)
                .sort((a, b) => {
                    const label_a = (a.label || '').toLowerCase();
                    const label_b = (b.label || '').toLowerCase();
                    return label_a.localeCompare(label_b);
                });
        },

        setDefaultSelection() {
            if (this.value.length === 0 && this.all_employees.length > 0) {
                const all_employees_option = this.all_employees.find(option => option.kind === 'all-employees')
                    || this.all_employees[0];

                this.$emit('input', [all_employees_option.selected_object_id]);
            }
        },

        loadEmployees() {
            if (!this.organization_id) {
                this.internal_all_employees = [];
                return;
            }

            this.loading = true;

            this.$rc.getEmployeeSelectorOptions(
                this.organization_id,

                (data) => {
                    this.loading = false;

                    const all_selector_options = data && data.all_selector_options ? data.all_selector_options : [];

                    this.internal_all_employees = this.processEmployeeOptions(all_selector_options);
                    this.setDefaultSelection();
                },

                () => {
                    this.loading = false;
                    this.$rc.alert('Error', 'Unable to load users');
                }
            );
        },

        addEmployee(employee_id) {
            const employee = this.all_employees.find(emp => emp.selected_object_id === employee_id);
            const all_employees_option = this.all_employees.find(emp => emp.kind === 'all-employees');
            
            let new_value;
            
            if (employee && employee.kind === 'all-employees') {
                new_value = [employee_id];
            }
            else if (all_employees_option && this.value.includes(all_employees_option.selected_object_id)) {
                new_value = [employee_id];
            }
            else {
                new_value = [...this.value, employee_id];
            }
            
            this.$emit('input', new_value);
            this.$emit('change', new_value);
        },

        removeEmployee(employee_id) {
            const new_value = this.value.filter(id => id !== employee_id);
            this.$emit('input', new_value);
            this.$emit('change', new_value);
        },

        selectAll() {
            const all_ids = this.all_employees.map(emp => emp.selected_object_id);
            this.$emit('input', all_ids);
            this.$emit('change', all_ids);
            this.menu_open = false;
        },

        removeAll() {
            this.$emit('input', []);
            this.$emit('change', []);
        },

        getIconForKind(kind) {
            const icon_map = {
                'employee': 'mdi-account',
                'group': 'mdi-account-group',
                'all-employees': 'mdi-office-building',
            };
            return icon_map[kind] || 'mdi-account';
        },
    },
};
</script>

<style scoped lang="scss">
.employee-selector {
    .inline-action :deep(.v-btn__content) {
        display: flex;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
    }

    .add-employee-card {
        cursor: pointer;
        transition: all 0.2s ease;
        border: 2px solid #e0e0e0;

        &:hover {
            border-color: var(--v-primary-base);
            background-color: #f5f5f5;
        }
    }

    .chips-container {
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        padding: 8px;
        min-height: 60px;
        max-height: 200px;
        overflow-y: auto;
    }

    .empty-state {
        border: 2px dashed #e0e0e0;
        border-radius: 4px;
        background-color: var(--color-bg-shaded-light);
    }
}
</style>
